import { useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Sidebar } from '@/components/Sidebar'
import { Header } from '@/components/Header'
import { SkipLink } from '@/components/SkipLink'
import { PasswordChangeModal } from '@/components/PasswordChangeModal'
import { ToastContainer } from 'react-toastify'

export function MainLayoutWithPasswordCheck() {
  const { user } = useAuth()
  const [showPasswordChange, setShowPasswordChange] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  useEffect(() => {
    // Check if user has temporary password and needs to change it
    if (user?.isPasswordTemporary) {
      setShowPasswordChange(true)
    }
  }, [user])

  useEffect(() => {
    if (!isSidebarOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsSidebarOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isSidebarOpen])

  const handlePasswordChangeSuccess = () => {
    setShowPasswordChange(false)
    // In a real app, you would update the user's password status in the backend
    // and refresh the user data
  }

  return (
    <>
      <div className="min-h-screen bg-secondary-50">
        {/* Skip Links for Accessibility */}
        <SkipLink href="#main-content">Skip to main content</SkipLink>
        <SkipLink href="#navigation">Skip to navigation</SkipLink>

        <div className="flex">
          {/* Sidebar */}
          <Sidebar
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
          />

          {/* Main Content */}
          <div className="flex-1 flex flex-col min-h-screen">
            <Header
              user={user}
              onToggleSidebar={() => setIsSidebarOpen((open) => !open)}
              isSidebarOpen={isSidebarOpen}
            />
            <main
              id="main-content"
              className="flex-1 p-6 max-h-screen overflow-y-auto"
              role="main"
              aria-label="Main content"
            >
              <Outlet />
            </main>
          </div>
        </div>
      </div>

      <PasswordChangeModal
        isOpen={showPasswordChange}
        onClose={() => { }} // Don't allow closing if it's a temporary password
        onSuccess={handlePasswordChangeSuccess}
        isTemporaryPassword={user?.isPasswordTemporary ?? false}
      />
    </>
  )
}
